import { createApp, nextTick } from 'vue';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import EstimateLandscapeDetailedPdf from '@/components/estimates/EstimateLandscapeDetailedPdf.vue';
import EstimatePortraitAnnualPdf from '@/components/estimates/EstimatePortraitAnnualPdf.vue';
import EstimatePortraitSimplePdf from '@/components/estimates/EstimatePortraitSimplePdf.vue';
import EstimatePortraitSimplePdfEn from '@/components/estimates/EstimatePortraitSimplePdfEn.vue';
import EstimatePortraitTemporaryPdf from '@/components/estimates/EstimatePortraitTemporaryPdf.vue';
import { formatRevisionLabel } from '@/utils/estimates';

const SUPPORTED_LAYOUT_TYPES = new Set([
  'portrait_simple',
  'landscape_detailed',
  'portrait_temporary',
  'portrait_annual'
]);
const SUPPORTED_LANGUAGE_TYPES = new Set(['japanese', 'english']);

export const isEstimatePdfSupported = estimate => (
  SUPPORTED_LAYOUT_TYPES.has(estimate?.output_layout_type) &&
  SUPPORTED_LANGUAGE_TYPES.has(estimate?.default_language_type)
);

const safeFilenamePart = value =>
  String(value || '').replace(/[\\/:*?"<>|]+/g, '_').trim();

export const estimatePdfFilename = (estimate, now = new Date()) => {
  const timestamp = [
    now.getFullYear(),
    String(now.getMonth() + 1).padStart(2, '0'),
    String(now.getDate()).padStart(2, '0'),
    String(now.getHours()).padStart(2, '0'),
    String(now.getMinutes()).padStart(2, '0'),
    String(now.getSeconds()).padStart(2, '0')
  ].join('');
  const isEnglish = estimate?.default_language_type === 'english';
  const revision = Number(estimate?.revision_no || 0) > 0
    ? `_${formatRevisionLabel(estimate.revision_no).replace('.', '')}`
    : '';
  const prefix = isEnglish ? 'Quotation' : '見積書';
  const estimateNumber = safeFilenamePart(estimate?.estimate_no) || 'プレビュー';
  return `${prefix}_${estimateNumber}${revision}_${timestamp}.pdf`;
};

const pdfComponentFor = estimate => {
  const components = {
    portrait_temporary: EstimatePortraitTemporaryPdf,
    portrait_annual: EstimatePortraitAnnualPdf,
    landscape_detailed: EstimateLandscapeDetailedPdf
  };
  return components[estimate.output_layout_type] || (
    estimate.default_language_type === 'english'
      ? EstimatePortraitSimplePdfEn
      : EstimatePortraitSimplePdf
  );
};

// 余白を含めたA41ページ分の画像を作る。PDFと画面プレビューで同じ画像を使い、見た目を一致させる。
const composePageImage = (canvas, pageWidth, pageHeight) => {
  const canvasRatio = canvas.width / canvas.height;
  const pageRatio = pageWidth / pageHeight;
  let imageWidth = pageWidth;
  let imageHeight = pageHeight;
  let imageX = 0;
  let imageY = 0;

  if (canvasRatio > pageRatio) {
    imageHeight = pageWidth / canvasRatio;
    imageY = (pageHeight - imageHeight) / 2;
  } else if (canvasRatio < pageRatio) {
    imageWidth = pageHeight * canvasRatio;
    imageX = (pageWidth - imageWidth) / 2;
  }

  const pixelsPerMm = canvas.width / imageWidth;
  const pageCanvas = document.createElement('canvas');
  pageCanvas.width = Math.round(pageWidth * pixelsPerMm);
  pageCanvas.height = Math.round(pageHeight * pixelsPerMm);
  const context = pageCanvas.getContext('2d');
  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
  context.drawImage(
    canvas,
    Math.round(imageX * pixelsPerMm),
    Math.round(imageY * pixelsPerMm),
    canvas.width,
    canvas.height
  );
  return pageCanvas.toDataURL('image/jpeg', 0.95);
};

const renderPdf = async (pdfElement, isLandscape) => {
  const pageElements = Array.from(
    pdfElement.querySelectorAll('[data-estimate-pdf-page]')
  );
  const printablePages = pageElements.length > 0 ? pageElements : [pdfElement];
  const orientation = isLandscape ? 'landscape' : 'portrait';
  const pageWidth = isLandscape ? 297 : 210;
  const pageHeight = isLandscape ? 210 : 297;
  const pdf = new jsPDF({
    unit: 'mm',
    format: 'a4',
    orientation,
    compress: true
  });
  const pageImages = [];

  for (let index = 0; index < printablePages.length; index += 1) {
    const pageElement = printablePages[index];
    const bounds = pageElement.getBoundingClientRect();
    const canvas = await html2canvas(pageElement, {
      scale: 2,
      useCORS: true,
      backgroundColor: '#ffffff',
      logging: false,
      scrollX: 0,
      scrollY: 0,
      width: Math.ceil(bounds.width),
      height: Math.ceil(bounds.height),
      windowWidth: Math.ceil(bounds.width),
      windowHeight: Math.ceil(bounds.height)
    });

    if (index > 0) pdf.addPage('a4', orientation);

    const pageImage = composePageImage(canvas, pageWidth, pageHeight);
    pageImages.push(pageImage);
    pdf.addImage(pageImage, 'JPEG', 0, 0, pageWidth, pageHeight, undefined, 'FAST');
  }

  return { blob: pdf.output('blob'), pageImages, orientation };
};

const waitForImages = async (rootElement) => {
  // Vueの描画と画像読み込みが完了してからPDF化する。
  // DOM更新を挟んで最大3回まで現在の画像を待つ。
  for (let attempt = 0; attempt < 3; attempt += 1) {
    await nextTick();
    const images = Array.from(rootElement.querySelectorAll('img'));
    await Promise.all(images.map((img) => {
      if (img.complete) {
        return typeof img.decode === 'function' ? img.decode().catch(() => undefined) : undefined;
      }
      return new Promise((resolve) => {
        const finish = () => {
          clearTimeout(timeoutId);
          img.removeEventListener('load', finish);
          img.removeEventListener('error', finish);
          resolve();
        };
        const timeoutId = setTimeout(finish, 5000);
        img.addEventListener('load', finish, { once: true });
        img.addEventListener('error', finish, { once: true });
      });
    }));
  }
};

export const createEstimatePdf = async estimate => {
  if (!isEstimatePdfSupported(estimate)) {
    throw new Error('現在の出力形式または言語には対応していません。');
  }

  const isLandscape = estimate.output_layout_type === 'landscape_detailed';
  const mountTarget = document.createElement('div');
  let pdfApp = null;

  try {
    Object.assign(mountTarget.style, {
      position: 'fixed',
      left: '-100000px',
      top: '0',
      width: isLandscape ? '297mm' : '210mm',
      background: '#fff'
    });
    document.body.appendChild(mountTarget);

    pdfApp = createApp(pdfComponentFor(estimate), { estimate });
    pdfApp.mount(mountTarget);
    await nextTick();
    if (document.fonts?.ready) await document.fonts.ready;
    await waitForImages(mountTarget);

    const pdfElement = mountTarget.querySelector('[data-estimate-pdf]');
    if (!pdfElement || pdfElement.dataset.estimatePdfReady !== 'true') {
      throw new Error('PDF出力用画面を準備できませんでした');
    }

    return {
      ...(await renderPdf(pdfElement, isLandscape)),
      filename: estimatePdfFilename(estimate)
    };
  } finally {
    if (pdfApp) pdfApp.unmount();
    if (mountTarget.parentNode) mountTarget.parentNode.removeChild(mountTarget);
  }
};

export const downloadBlob = (blob, filename) => {
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
};
